import React, { useState } from 'react';
import {
  TableContainer,
  TableHead,
  Table,
  TableCell,
  TableRow,
  TableBody,
  Box,
  Typography,
  useTheme,
  LinearProgress,
  ButtonBase,
  TablePagination,
  Chip,
  Stack,
  Avatar,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { IconArrowRight } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from 'src/utils';
import { toast } from 'react-toastify';
import { unAssignDistributor } from 'src/store/actions/users.action';
import config from '../../config';
import EmptyComponent from '../EmptyComponent';

export default function UserDistributorsTable({ users, loading, onFinished }) {
  const globalTheme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { palette } = globalTheme;
  const { routes } = config;

  const { roles, userStatuses } = useSelector((state) => state.app);
  const { unAssignDistributorStatus } = useSelector((st) => st.users);

  const [limit, setLimit] = useState(3);
  const [page, setPage] = useState(0);

  const unAssignAgentFromDistributor = async (item) => {
    try {
      const payload = { salesUserId: id, distributorId: item.id };
      const response = await dispatch(unAssignDistributor(payload)).unwrap();

      if (response) {
        toast.success('Distributor unassigned');
        onFinished();
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const headers = [
    { id: 1, label: 'Distributor', key: 'user' },
    { id: 2, label: 'Phone Number', key: 'phone' },
    { id: 3, label: 'Email', key: 'email' },
    { id: 4, label: 'Date', key: 'createdAt' },
    { id: 6, label: 'Status', key: 'status' },
    { id: 7, label: 'Action', key: 'action' },
  ];

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const buildStatus = (statusId) => {
    let color = 'warning';
    const selectedStatus = userStatuses.find((item) => item.id === statusId);
    if (!selectedStatus) return { value: 'Uknown', color };

    if (selectedStatus.abbreviation === 'ACT') {
      color = 'success';
    } else if (selectedStatus.abbreviation === 'DSD') {
      color = 'default';
    } else if (selectedStatus.abbreviation === 'SSD') {
      color = 'error';
    } else if (selectedStatus.abbreviation === 'PND') {
      color = 'warning';
    } else {
      color = 'error';
    }

    return { value: selectedStatus.name, color };
  };

  return (
    <>
      {(loading || unAssignDistributorStatus === 'loading') && (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
      )}

      <TableContainer sx={{
        borderWidth: '1px',
        borderColor: palette.grey[300],
        borderStyle: 'solid',
        borderRadius: '16px',
      }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {headers.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                >
                  <Typography sx={{ fontWeight: 'normal' }}>{column.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {!loading && users && users.length === 0 && (
          <TableBody>
            <TableRow style={{ height: 300 }}>
              <TableCell colSpan={12}>
                <EmptyComponent />
              </TableCell>
            </TableRow>
          </TableBody>
          )}

          {users && users.length > 0 && (
          <TableBody sx={{ }}>

            {(limit > 0
              ? users.slice(page * limit, page * limit + limit)
              : users
            ).map((item) => (

              <TableRow
                hover
                key={item.id}
              >
                <TableCell sx={{}}>
                  <Stack direction="row" alignContent="center" alignItems="center">
                    <Avatar alt="Profile pic" src={item.profilePicture} />
                    <Typography sx={{ ml: 1 }}>
                      {item.firstName}
                      {' '}
                      {item.lastName}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{item.phone}</TableCell>
                <TableCell>{item.email}</TableCell>
                <TableCell>
                  {formatDate(item.createdAt)}
                </TableCell>

                <TableCell>
                  <Chip
                    color={buildStatus(item.statusId).color}
                    label={buildStatus(item.statusId).value}
                    sx={{ minWidth: '80px' }}
                  />
                </TableCell>

                <TableCell>
                  <ButtonBase
                    sx={{
                      borderRadius: '30px',
                      px: 2,
                      py: '4px',
                      backgroundColor: palette.secondary.light,
                    }}
                    disabled={unAssignDistributorStatus === 'loading'}
                    onClick={() => unAssignAgentFromDistributor(item)}
                  >
                    <Typography color="white">Unassign</Typography>
                  </ButtonBase>

                </TableCell>

              </TableRow>
            ))}
          </TableBody>
          )}
        </Table>
      </TableContainer>

      {users && users.length > 0 && (
        <TablePagination
          component="div"
          count={users.length}
          page={page}
          colSpan={3}
          rowsPerPage={limit}
          rowsPerPageOptions={[5, 10, 15, { label: 'All', value: -1 }]}
          SelectProps={{
            inputProps: {
              'aria-label': 'users per page',
            },
            native: true,
          }}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
        />
      )}
    </>
  );
}

UserDistributorsTable.propTypes = {
  users: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onFinished: PropTypes.func,
};
